import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PipecatClient, RTVIEvent } from "@pipecat-ai/client-js";
import {
  PipecatClientAudio,
  PipecatClientProvider,
  usePipecatClient,
  useRTVIClientEvent,
} from "@pipecat-ai/client-react";
import { DailyTransport } from "@pipecat-ai/daily-transport";
import { MdCall, MdCampaign } from "react-icons/md";

const API = process.env.REACT_APP_API_URL;
const TERMINAL_STATUSES = new Set(["declined", "missed", "ended"]);

function formatCallDuration(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return [hours, minutes, seconds].map((value) => String(value).padStart(2, "0")).join(":");
  }
  return [minutes, seconds].map((value) => String(value).padStart(2, "0")).join(":");
}

function isExpectedCallAbort(error) {
  const details = [
    error?.name,
    error?.message,
    error?.type,
    error?.code,
  ].filter(Boolean).join(" ");

  return /abort|aborted/i.test(details);
}

function PatientCallButtonInner({ patientId, disabled = false, isDeviceOnline = true }) {
  const client = usePipecatClient();
  const [call, setCall] = useState(null);
  const [startingCall, setStartingCall] = useState(false);
  const [endingCall, setEndingCall] = useState(false);
  const [callError, setCallError] = useState("");
  const [ringPhase, setRingPhase] = useState(false);
  const [, setDurationTick] = useState(0);
  const [announcementEndsAt, setAnnouncementEndsAt] = useState(null);
  const [, setAnnouncementTick] = useState(0);
  const activeCallRef = useRef({ id: null, status: null });

  useEffect(() => {
    activeCallRef.current = {
      id: call?.id ?? null,
      status: call?.status ?? null,
    };
  }, [call?.id, call?.status]);

  useRTVIClientEvent(
    RTVIEvent.ParticipantLeft,
    useCallback(() => {
      if (activeCallRef.current.status === "accepted") {
        activeCallRef.current = { id: null, status: null };
        setCall(null);
      }
    }, [])
  );

  useEffect(() => {
    if (call?.status !== "ringing") {
      setRingPhase(false);
      return undefined;
    }

    const interval = window.setInterval(() => {
      setRingPhase((prev) => !prev);
    }, 700);

    return () => window.clearInterval(interval);
  }, [call?.status]);

  useEffect(() => {
    if (call?.status !== "accepted" || !call?.accepted_at) {
      setDurationTick(0);
      return undefined;
    }

    const interval = window.setInterval(() => {
      setDurationTick((prev) => prev + 1);
    }, 1000);

    return () => window.clearInterval(interval);
  }, [call?.status, call?.accepted_at]);

  useEffect(() => {
    if (call?.status !== "ringing" || call?.mode !== "announcement") {
      setAnnouncementEndsAt(null);
      setAnnouncementTick(0);
      return undefined;
    }

    if (!announcementEndsAt) {
      const createdAtMs = call?.created_at ? new Date(call.created_at).getTime() : Date.now();
      setAnnouncementEndsAt(createdAtMs + 3000);
    }

    const interval = window.setInterval(() => {
      setAnnouncementTick((prev) => prev + 1);
    }, 250);

    return () => window.clearInterval(interval);
  }, [announcementEndsAt, call?.created_at, call?.mode, call?.status]);

  useEffect(() => {
    if (!call?.id) return undefined;

    let cancelled = false;
    const interval = window.setInterval(async () => {
      try {
        const res = await fetch(`${API}/calls/${call.id}`, {
          credentials: "include",
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error || "Call status could not be loaded.");
        }
        if (cancelled) return;

        const nextCall = data.call || null;
        if (!nextCall || TERMINAL_STATUSES.has(nextCall.status)) {
          activeCallRef.current = { id: null, status: null };
          try {
            await client.disconnect();
          } catch (_) {
            // ignore disconnect races
          }
          setCall(null);
          return;
        }

        setCall(nextCall);
      } catch (e) {
        if (!cancelled) {
          setCallError(e?.message || "Call status could not be loaded.");
        }
      }
    }, 2000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [call?.id, client]);

  useEffect(() => {
    return () => {
      client.disconnect().catch(() => {});
    };
  }, [client]);

  async function handleStartCall(mode = "call") {
    if (!isDeviceOnline) {
      setCallError("Gerät ist offline. Bitte an Strom anschließen.");
      return;
    }
    setStartingCall(true);
    setCallError("");
    try {
      const res = await fetch(`${API}/patients/${patientId}/calls`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "Anruf konnte nicht gestartet werden.");
      }

      const nextCall = data.call;
      const connection = nextCall?.connection || {};
      if (!connection.dailyRoom || !connection.dailyToken) {
        throw new Error("Call connection details are missing.");
      }

      setCall(nextCall);
      setAnnouncementEndsAt(mode === "announcement" ? Date.now() + 3000 : null);

      void client.connect({
        dailyRoom: connection.dailyRoom,
        dailyToken: connection.dailyToken,
      }).catch((e) => {
        if (isExpectedCallAbort(e)) {
          return;
        }

        const activeCall = activeCallRef.current;
        if (
          activeCall.id !== nextCall.id ||
          TERMINAL_STATUSES.has(activeCall.status)
        ) {
          return;
        }
        setCallError(e?.message || "Anrufverbindung konnte nicht aufgebaut werden.");
      });
    } catch (e) {
      setCallError(e?.message || "Anruf konnte nicht gestartet werden.");
    } finally {
      setStartingCall(false);
    }
  }

  async function handleEndCall() {
    setEndingCall(true);
    setCallError("");
    const callId = call?.id;
    try {
      activeCallRef.current = { id: null, status: null };
      setCall(null);

      try {
        await client.disconnect();
      } catch (_) {
        // ignore disconnect races
      }

      if (callId) {
        const res = await fetch(`${API}/calls/${callId}/end`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ended_by: "caller" }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          console.warn("Call end sync failed", data?.error || res.status);
        }
      }
    } catch (e) {
      setCallError(e?.message || "Anruf konnte nicht beendet werden.");
    } finally {
      setEndingCall(false);
    }
  }

  const buttonState = call?.status === "accepted" ? "accepted" : call?.status === "ringing" ? "ringing" : "idle";
  const isDisabled = disabled || startingCall || endingCall;
  const startDisabled = isDisabled || !isDeviceOnline;
  const announcementSecondsLeft = announcementEndsAt ? Math.max(0, Math.ceil((announcementEndsAt - Date.now()) / 1000)) : 0;
  const label = buttonState === "accepted" ? "Auflegen" : buttonState === "ringing" ? (call?.mode === "announcement" ? `Durchsage in ${announcementSecondsLeft} ${announcementSecondsLeft === 1 ? "Sekunde" : "Sekunden"}` : "Klingelt") : "Anrufen";
  const acceptedAtMs = call?.accepted_at ? new Date(call.accepted_at).getTime() : null;
  const durationSeconds = acceptedAtMs ? Math.max(0, Math.floor((Date.now() - acceptedAtMs) / 1000)) : 0;

  const className = [
    "inline-flex w-[132px] justify-center rounded-xl px-4 py-2 text-sm font-medium text-white shadow-sm transition disabled:cursor-not-allowed",
    buttonState === "accepted"
      ? "bg-red-600 hover:bg-red-700 disabled:bg-red-300"
      : buttonState === "ringing"
        ? ringPhase
          ? "bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300"
          : "bg-yellow-400 hover:bg-yellow-500 disabled:bg-yellow-300"
        : "bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300",
  ].join(" ");

  return (
    <div className="relative flex flex-col items-end gap-2">
      {!call && !startingCall && !callError && !isDeviceOnline && (
        <div className="absolute bottom-full right-0 mb-1 w-full text-right text-xs font-medium leading-4 text-red-600">
          Gerät offline. Bitte an Strom anschließen
        </div>
      )}
      <div className="flex items-center gap-3">
        {buttonState === "accepted" && (
          <div className="min-w-[64px] rounded-lg bg-white/80 px-3 py-1.5 text-center text-sm font-medium tabular-nums text-gray-700">
            {formatCallDuration(durationSeconds)}
          </div>
        )}
        {buttonState === "idle" ? (
          <>
            <button
              onClick={() => handleStartCall("call")}
              disabled={startDisabled}
              className="inline-flex w-[132px] items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
            >
              <MdCall className="text-base" />
              Anrufen
            </button>
            <button
              onClick={() => handleStartCall("announcement")}
              disabled={startDisabled}
              className="inline-flex w-[132px] items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-sky-300"
            >
              <MdCampaign className="text-base" />
              Durchsage
            </button>
          </>
        ) : (
          <button
            onClick={handleEndCall}
            disabled={isDisabled}
            className={className}
          >
            {label}
          </button>
        )}
      </div>
      {callError && (
        <div className="max-w-xs rounded-md border border-red-200 bg-red-50 px-3 py-2 text-right text-sm text-red-800">
          {callError}
        </div>
      )}
      {startingCall && !call && !callError && (
        <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-right text-sm text-sky-800">
          Verbindung wird aufgebaut...
        </div>
      )}
      <PipecatClientAudio />
    </div>
  );
}

export default function PatientCallButton({ patientId, disabled = false, isDeviceOnline = true }) {
  const client = useMemo(
    () =>
      new PipecatClient({
        transport: new DailyTransport(),
        enableMic: true,
        enableCam: false,
      }),
    []
  );

  return (
    <PipecatClientProvider client={client}>
      <PatientCallButtonInner patientId={patientId} disabled={disabled} isDeviceOnline={isDeviceOnline} />
    </PipecatClientProvider>
  );
}
